import { Box, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { forwardRef, useMemo } from "react"
import { RoadmapImplementationType, RoadmapItem, RoadmapItemType } from "../newsTypes"
import { useDateTime } from "../../localization/useDateTime"
import { Stack } from "@mui/system"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { Link } from "react-router-dom"
import { faArrowRight } from "@fortawesome/pro-light-svg-icons"

const useStyles = makeStyles()((theme) => ({
    card: {
        backgroundColor: theme.palette.grey[200],
        padding: "1rem",
        marginBottom: "1.25rem",
    },
    header: {
        display: "flex",
        alignItems: "center",
    },
    date: {
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
    },
    title: {
        fontSize: "1.825rem",
        fontWeight: 400,
        color: theme.palette.text.primary,
        lineHeight: 1.14,
    },
    link: {
        color: theme.palette.text.secondary,
        textDecoration: "none",
        fontSize: "0.875rem",
        "&:hover": {
            textDecoration: "underline",
            color: theme.palette.primary.main,
        },
    },
    description: {
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    linkContainer: {
        flexDirection: "row",
        marginTop: "1.5rem",
        alignItems: "center",
        gap: "0.625rem",
        textDecoration: "none",
    },
    dot: {
        margin: "0 .5rem",
        color: "#94A3B8",
    },
    type: {
        color: theme.palette.tailwind.green[800],
        fontSize: "0.875rem",
        lineHeight: 1.66,
        fontWeight: 700,
        textTransform: "capitalize",
    },
}))

interface RoadmapFeatureCardProps {
    item: RoadmapItem
    isUser?: boolean
}

export const RoadmapFeatureCard = forwardRef<HTMLDivElement, RoadmapFeatureCardProps>(({ item, isUser }, ref) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const details = item.roadmapDetails
    const implementationDate =
        details.itemType === RoadmapItemType.IMPLEMENTED ? details.implementationDate : undefined
    const dateSource = implementationDate || item.publishDate
    const formattedDate = dateTimeFactory.formatDate(dateTimeFactory.fromApi(dateSource))

    const typeLabel = useMemo(() => {
        if (details.implementationType === RoadmapImplementationType.FEATURE) {
            return details.itemType === RoadmapItemType.IMPLEMENTED ? (
                <LocalizedStrict id="news-implemented-feature">
                    <span>Implemented feature</span>
                </LocalizedStrict>
            ) : (
                <LocalizedStrict id="news-upcoming-feature">
                    <span>Upcoming feature</span>
                </LocalizedStrict>
            )
        }
        return (
            <LocalizedStrict id="news-bug-fix">
                <span>Bug fix</span>
            </LocalizedStrict>
        )
    }, [details.implementationType, details.itemType])

    return (
        <Box className={classes.card} ref={ref} data-permalink={item.permaLink}>
            <Stack gap="1rem">
                <Box className={classes.header}>
                    <Typography variant="caption" className={classes.date}>
                        {details.itemType === RoadmapItemType.UPCOMING ? (
                            <LocalizedStrict id="news-published" vars={{ date: formattedDate }}>
                                <span>Publishes: {formattedDate}</span>
                            </LocalizedStrict>
                        ) : implementationDate ? (
                            formattedDate
                        ) : (
                            <LocalizedStrict id="news-created" vars={{ date: formattedDate }}>
                                <span>Created: {formattedDate}</span>
                            </LocalizedStrict>
                        )}
                    </Typography>
                    {details.implementationType && (
                        <>
                            <FontAwesomeIcon className={classes.dot} width=".25rem" icon={faCircle} />
                            <Typography className={classes.type}>{typeLabel}</Typography>
                        </>
                    )}
                </Box>

                {item.subject && <Typography className={classes.title}>{item.subject}</Typography>}

                {(item.description || item.shortDescription) && (
                    <Typography variant="body2" color="textPrimary" className={classes.description}>
                        {item.description ?? item.shortDescription}
                    </Typography>
                )}
            </Stack>

            <Link to={item.permaLink} className={classes.link} target="_blank" rel="noopener noreferrer">
                <Stack className={classes.linkContainer}>
                    <LocalizedStrict id="news-gopay-read-more">
                        <Typography variant="body2">Read more</Typography>
                    </LocalizedStrict>
                    <FontAwesomeIcon icon={faArrowRight} />
                </Stack>
            </Link>
        </Box>
    )
})

RoadmapFeatureCard.displayName = "RoadmapFeatureCard"
